// src/components/User/InviteUserDialog.tsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from '@mui/material';
import {
  inviteUser,
  InviteUserRequest,
  InviteUserResponse,
  UserApiError,
} from '../../services/userApi';
import { getUserAccessToken } from '../../utils/tokenStorage';
import { getRoleDisplayName } from '../../utils/roleDisplayNames';

interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Identity role names (value = internal; label = user-facing, Sprint J: Tenant User display)
const IDENTITY_ROLES = [
  { value: 'System Admin', label: 'System Admin' },
  { value: 'Exec Admin', label: 'Exec Admin' },
  { value: 'Airport Manager', label: 'Airport Manager' },
  { value: 'Fraport User', label: 'Fraport User' },
  { value: 'Client User', label: 'Client User' },
  { value: 'Tenant User (365 Tenant Sense)', label: getRoleDisplayName('Tenant User (365 Tenant Sense)') },
];

const InviteUserDialog: React.FC<InviteUserDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [email, setEmail] = useState('');
  const [roleName, setRoleName] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [invitationLink, setInvitationLink] = useState<string | null>(null);

  // Reset form when dialog opens/closes
  React.useEffect(() => {
    if (open) {
      setEmail('');
      setRoleName('');
      setError(null);
      setValidationErrors({});
      setInvitationLink(null);
    }
  }, [open]);

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Email validation
    if (!email.trim()) {
      errors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        errors.email = 'Invalid email address format';
      }
    }

    // Role validation
    if (!roleName) {
      errors.roleName = 'Role is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setError(null);
    setIsSubmitting(true);

    try {
      // Get user access token for delegated authentication (same as project invitations)
      const userAccessToken = getUserAccessToken();
      
      const inviteData: InviteUserRequest = {
        email: email.trim(),
        roleName: roleName,
        userAccessToken: userAccessToken || null, // Include token for delegated auth
      };

      const response: InviteUserResponse = await inviteUser(inviteData);
      
      // Store invitation link for display
      setInvitationLink(response.invitationLink);
      
      // Show success message and close after a delay
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } catch (err) {
      const apiError = err as UserApiError;
      console.error('InviteUserDialog: Error inviting user:', err);
      
      // Provide more helpful error messages
      let errorMessage = apiError.message || 'Failed to invite user';
      
      if (apiError.code === 'NETWORK_ERROR') {
        errorMessage = apiError.message || 'Network error: Unable to connect to the backend server. Please check the backend server logs.';
      } else if (apiError.code === 'AUTH_REQUIRED') {
        errorMessage = 'Authentication required. Please refresh the page and try again.';
      } else if (apiError.code === 'VALIDATION_ERROR') {
        errorMessage = apiError.message || 'Validation failed. Please check your input.';
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setError(null);
      setValidationErrors({});
      setInvitationLink(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite User</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {invitationLink && (
            <Alert severity="success">
              Invitation sent successfully! The user will receive an email with a registration link.
              {process.env.NODE_ENV === 'development' && (
                <Box sx={{ mt: 1, fontSize: '0.875rem' }}>
                  <strong>Dev Link:</strong> <a href={invitationLink} target="_blank" rel="noopener noreferrer">{invitationLink}</a>
                </Box>
              )}
            </Alert>
          )}

          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setValidationErrors({ ...validationErrors, email: '' });
            }}
            required
            fullWidth
            autoFocus
            disabled={isSubmitting || !!invitationLink}
            error={!!validationErrors.email}
            helperText={validationErrors.email || 'Email address for the new user'}
          />

          <FormControl fullWidth required error={!!validationErrors.roleName} disabled={isSubmitting || !!invitationLink}>
            <InputLabel>Role</InputLabel>
            <Select
              value={roleName}
              label="Role"
              onChange={(e) => {
                setRoleName(e.target.value);
                setValidationErrors({ ...validationErrors, roleName: '' });
              }}
            >
              {IDENTITY_ROLES.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  {role.label}
                </MenuItem>
              ))}
            </Select>
            {validationErrors.roleName && (
              <FormHelperText>{validationErrors.roleName}</FormHelperText>
            )}
            {!validationErrors.roleName && (
              <FormHelperText>Select the role to assign to the new user</FormHelperText>
            )}
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          {invitationLink ? 'Close' : 'Cancel'}
        </Button>
        {!invitationLink && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send Invitation'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default InviteUserDialog;
